/**
 * OSD (Operator Systemu Dystrybucyjnego) Metadata
 *
 * Frontend metadata for all 4 Polish electricity distributors.
 * Provides display names, descriptions, coverage, and available tariffs.
 *
 * @see /amplify/data/config/osd-metadata.json - Source data
 * @see /amplify/data/utils/osd-types.ts - Type definitions
 */

import { TariffType } from '../types/graphql';

// ============================================================================
// OSD TYPES (Frontend-specific)
// ============================================================================

/**
 * OSD operator codes
 */
export type OSDCode = 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA';

/**
 * All supported OSD codes
 */
export const OSD_CODES: readonly OSDCode[] = ['TAURON', 'PGE', 'ENEA', 'ENERGA'] as const;

/**
 * eLicznik Portal Metadata
 * Smart meter portal information for each OSD
 */
export interface ELicznikPortal {
  name: string;
  url: string;
  description: string;
}

/**
 * OSD Metadata Interface
 * Complete metadata for a single OSD operator
 */
export interface OSDMetadata {
  code: OSDCode;
  name: string;
  shortName: string;
  logo: string;
  website: string;
  coverage: string[];
  customersCount?: number;
  availableTariffs: TariffType[];
  color: string;
  description: string;
  /** Smart meter portal for hourly consumption data */
  elicznikPortal: ELicznikPortal;
}

// ============================================================================
// OSD METADATA REGISTRY
// ============================================================================

/**
 * OSD Metadata Registry
 * Complete metadata for all 4 Polish electricity distributors
 */
export const OSD_METADATA: Record<OSDCode, OSDMetadata> = {
  TAURON: {
    code: 'TAURON',
    name: 'TAURON® Dystrybucja S.A.',
    shortName: 'Tauron',
    logo: '/assets/osd/tauron-logo.svg',
    website: 'https://www.tauron-dystrybucja.pl',
    coverage: [
      'dolnośląskie',
      'małopolskie',
      'opolskie',
      'śląskie',
      'świętokrzyskie (część)',
      'podkarpackie (część)',
      'łódzkie (część)',
    ],
    customersCount: 5700000,
    availableTariffs: [
      TariffType.G11,
      TariffType.G12,
      TariffType.G12w,
      TariffType.G12as,
      TariffType.G13,
      TariffType.G13s,
      TariffType.G14,
    ],
    color: '#6B2D5B', // Tauron purple
    description: 'Największy dystrybutor energii w Polsce południowej',
    elicznikPortal: {
      name: 'eLicznik',
      url: 'https://elicznik.tauron-dystrybucja.pl',
      description: 'Portal do pobierania danych godzinowych z inteligentnego licznika',
    },
  },

  PGE: {
    code: 'PGE',
    name: 'PGE® Dystrybucja S.A.',
    shortName: 'PGE',
    logo: '/assets/osd/pge-logo.svg',
    website: 'https://pgedystrybucja.pl',
    coverage: [
      'mazowieckie',
      'łódzkie',
      'lubelskie',
      'podkarpackie (część)',
      'świętokrzyskie (część)',
      'podlaskie',
    ],
    customersCount: 5500000,
    availableTariffs: [
      TariffType.G11,
      TariffType.G12,
      TariffType.G12w,
      TariffType.G12n,  // PGE-specific: Nocna (tania noc 01:00-05:00 + weekendy)
    ],
    color: '#E31E24', // PGE red
    description: 'Dystrybutor energii w Polsce centralnej i wschodniej',
    elicznikPortal: {
      name: 'eBOK PGE',
      url: 'https://ebok.gkpge.pl',
      description: 'Elektroniczne Biuro Obsługi Klienta z danymi z Liczników Zdalnego Odczytu (LZO)',
    },
  },

  ENEA: {
    code: 'ENEA',
    name: 'ENEA® Operator Sp. z o.o.',
    shortName: 'Enea',
    logo: '/assets/osd/enea-logo.svg',
    website: 'https://www.operator.enea.pl',
    coverage: [
      'wielkopolskie',
      'zachodniopomorskie',
      'kujawsko-pomorskie',
      'lubuskie',
      'dolnośląskie (część)',
      'pomorskie (część)',
    ],
    customersCount: 2600000,
    availableTariffs: [
      TariffType.G11,
      TariffType.G12,
      TariffType.G12w,
      TariffType.G12as,
    ],
    color: '#00A651', // Enea green
    description: 'Dystrybutor energii w Polsce zachodniej',
    elicznikPortal: {
      name: 'Portal Odbiorcy',
      url: 'https://portalodbiorcy.operator.enea.pl',
      description: 'Portal do analizy danych z liczników zdalnego odczytu (LZO)',
    },
  },

  ENERGA: {
    code: 'ENERGA',
    name: 'ENERGA®-OPERATOR S.A.',
    shortName: 'Energa',
    logo: '/assets/osd/energa-logo.svg',
    website: 'https://www.energa-operator.pl',
    coverage: [
      'pomorskie',
      'warmińsko-mazurskie',
      'kujawsko-pomorskie (część)',
      'mazowieckie (część)',
      'wielkopolskie (część)',
    ],
    customersCount: 3200000,
    availableTariffs: [
      TariffType.G11,
      TariffType.G12,
      TariffType.G12w,
      TariffType.G12as,
      TariffType.G12r,  // Energa-specific: Rozszerzona weekendowa (dłuższa noc 21:00-07:00)
    ],
    color: '#F7A823', // Energa orange/yellow
    description: 'Dystrybutor energii w Polsce północnej',
    elicznikPortal: {
      name: 'Mój Licznik',
      url: 'https://mojlicznik.energa-operator.pl',
      description: 'Portal do monitorowania zużycia energii z liczników zdalnego odczytu',
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get OSD Metadata
 * Retrieve metadata for a specific OSD operator
 *
 * @param osdCode - OSD code (e.g., 'TAURON', 'PGE')
 * @returns OSD metadata
 */
export function getOSDMetadata(osdCode: OSDCode): OSDMetadata {
  return OSD_METADATA[osdCode];
}

/**
 * Get All OSD Codes
 * Get array of all supported OSD codes
 *
 * @returns Array of OSD codes
 */
export function getAllOSDCodes(): OSDCode[] {
  return [...OSD_CODES];
}

/**
 * Get OSD Display Name
 * Get full display name for an OSD
 *
 * @param osdCode - OSD code
 * @returns Display name (e.g., 'TAURON Dystrybucja S.A.')
 */
export function getOSDDisplayName(osdCode: OSDCode): string {
  return OSD_METADATA[osdCode].name;
}

/**
 * Get OSD Short Name
 * Get short display name for an OSD
 *
 * @param osdCode - OSD code
 * @returns Short name (e.g., 'Tauron')
 */
export function getOSDShortName(osdCode: OSDCode): string {
  return OSD_METADATA[osdCode].shortName;
}

/**
 * Get Available Tariffs for OSD
 * Get array of tariff codes available for a specific OSD
 *
 * @param osdCode - OSD code
 * @returns Array of TariffType codes
 */
export function getAvailableTariffsForOSD(osdCode: OSDCode): TariffType[] {
  return [...OSD_METADATA[osdCode].availableTariffs];
}

/**
 * Is Tariff Available for OSD
 * Check if a tariff is available for a specific OSD
 *
 * @param osdCode - OSD code
 * @param tariffCode - Tariff code
 * @returns True if tariff is available
 */
export function isTariffAvailableForOSD(osdCode: OSDCode, tariffCode: TariffType): boolean {
  return OSD_METADATA[osdCode].availableTariffs.includes(tariffCode);
}

/**
 * Get OSD Color
 * Get brand color for an OSD
 *
 * @param osdCode - OSD code
 * @returns Hex color code
 */
export function getOSDColor(osdCode: OSDCode): string {
  return OSD_METADATA[osdCode].color;
}

/**
 * Get OSD Coverage
 * Get coverage regions for an OSD
 *
 * @param osdCode - OSD code
 * @returns Array of voivodeship names
 */
export function getOSDCoverage(osdCode: OSDCode): string[] {
  return [...OSD_METADATA[osdCode].coverage];
}

/**
 * Get OSD eLicznik Portal
 * Get smart meter portal information for an OSD
 *
 * @param osdCode - OSD code
 * @returns eLicznik portal metadata
 */
export function getOSDElicznikPortal(osdCode: OSDCode): ELicznikPortal {
  return OSD_METADATA[osdCode].elicznikPortal;
}

/**
 * Is Valid OSD Code
 * Check if a string is a valid OSD code
 *
 * @param code - String to check
 * @returns True if valid OSD code
 */
export function isValidOSDCode(code: string): code is OSDCode {
  return OSD_CODES.includes(code as OSDCode);
}

/**
 * Get Default OSD
 * Returns the default OSD (TAURON - largest by customers)
 *
 * @returns Default OSD code
 */
export function getDefaultOSD(): OSDCode {
  return 'TAURON';
}

/**
 * Get All OSD Metadata
 * Get array of all OSD metadata objects
 *
 * @returns Array of OSD metadata
 */
export function getAllOSDMetadata(): OSDMetadata[] {
  return Object.values(OSD_METADATA);
}

/**
 * Get OSD Options for Select
 * Get array of options suitable for Select component
 *
 * @returns Array of { value, label } options
 */
export function getOSDSelectOptions(): { value: OSDCode; label: string }[] {
  return OSD_CODES.map((code) => ({
    value: code,
    label: OSD_METADATA[code].shortName,
  }));
}

export default {
  OSD_CODES,
  OSD_METADATA,
  getOSDMetadata,
  getAllOSDCodes,
  getOSDDisplayName,
  getOSDShortName,
  getAvailableTariffsForOSD,
  isTariffAvailableForOSD,
  getOSDColor,
  getOSDCoverage,
  getOSDElicznikPortal,
  isValidOSDCode,
  getDefaultOSD,
  getAllOSDMetadata,
  getOSDSelectOptions,
};
